import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/cn'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-accent hover:bg-accent-hover text-white shadow-[0_0_20px_rgba(94,106,210,0.15)] hover:shadow-[0_0_30px_rgba(94,106,210,0.25)]',
        secondary:
          'bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 text-white/80 hover:text-white',
        ghost: 'hover:bg-white/5 text-white/60 hover:text-white',
        danger: `bg-error hover:bg-red-500 text-white`,
        action:
          'flex flex-col bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 text-white/80 hover:text-white',
        blue:
          'bg-blue-500 hover:bg-blue-600 text-white shadow-[0_0_20px_rgba(59,130,246,0.2)] hover:shadow-[0_0_30px_rgba(59,130,246,0.3)]',
        purpleTint:
          'bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 text-purple-200',
        cyanTint:
          'bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/50 text-cyan-200',
        orangeTint:
          'bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/50 text-orange-200',
      },
      size: {
        sm: 'text-xs px-2 py-1',
        default: 'text-sm px-3 py-2',
        lg: 'text-base px-4 py-2.5',
        icon: 'p-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: ReactNode
}

export function Button({
  children,
  className,
  variant,
  size,
  ...props
}: ButtonProps) {
  return (
    <button className={cn(buttonVariants({ variant, size }), className)} {...props}>
      {children}
    </button>
  )
}
